'use client';
/* eslint-disable tailwindcss/no-arbitrary-value -- the tickets fix these in pixels (34/54px name block, 10px trip box foot, 122px logo, 30px CTA, 24px desktop add icon) and no token carries them */

import classNames from 'classnames';
import type { ReactNode } from 'react';
import {
  formatPremiumAmount,
  premiumCurrency,
} from '../../../calculatedAttributes/providers/tiQuote';
import { ApplyCtaLink } from '../../../components/ApplyCta';
import { CmsImage } from '../../../components/CmsImage';
import { MaterialIcon } from '../../../components/MaterialIcon';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { tr } from '../../../utils/translations/tr';
import { compareIdOf } from '../compareUrl';
import { compareImageOf, type CompareVariantLayout } from '../compareVariant';
import type { CompareCtaTone, ComparePlan } from '../types';
import {
  CompareRail,
  compareColumnClass,
  compareLabelClass,
  Pulse,
} from './CompareRail';

export type PlanColumnsProps = {
  plans: ComparePlan[];
  bestPremiumIndex: number | null;
  /** Dashed add columns after the plans: 0 at the cap or with nothing to add. */
  addSlots: number;
  addLabel: string;
  /**
   * What the variant puts in a column: whose picture, whether the issuer is named, and whether a
   * price is coming (so a loading card keeps room for it).
   */
  layout: Pick<
    CompareVariantLayout,
    'columnImage' | 'issuerLine' | 'pricesTrip'
  >;
  /**
   * The CTA's label and colour: `cta` (orange) for a checkout that completes here, `primary` (blue)
   * for one that leaves for the provider.
   */
  ctaFor: (plan: ComparePlan) => { label: string; tone: CompareCtaTone };
  /** The × on a plan's card; `index` is its column. */
  onRemove: (plan: ComparePlan, index: number) => void;
  /** `column` is the empty column clicked, counted after the plans. */
  onAddPlan: (column: number) => void;
  /** A plan's CTA was clicked; `label` is the text the reader saw on it. */
  onCtaClick?: (plan: ComparePlan, index: number, label: string) => void;
  staticText: Record<string, string>;
  /** Rendered in the label column beside the cards (the trip details box on TI). */
  labelSlot?: ReactNode;
  /** Compact variant for the sticky drawer: name, price, CTA — no logo, no usual price. */
  compact?: boolean;
  /** Columns drawn as loading cards before any plan has arrived, at a card's height. */
  placeholders?: number;
};

/**
 * The premium as the results card prints it. What counts as a price, and whether the usual one is
 * struck through, is decided once, where the card decides it (`resolveTiPremiumPrice`); this only
 * prints them the way every premium surface prints them.
 */
export function formatPremium(
  plan: ComparePlan,
  brandCurrency: string | undefined,
): {
  offered?: string;
  original?: string;
} {
  const premium = plan.premium;
  if (!premium) return {};
  const currency = premiumCurrency(premium, brandCurrency) ?? '';
  return {
    offered: `${currency}${formatPremiumAmount(premium.amount)}`,
    original:
      premium.originalAmount === undefined
        ? undefined
        : `${currency}${formatPremiumAmount(premium.originalAmount)}`,
  };
}

/**
 * One header card per compared plan on the shared column grid, plus the dashed "Add Plan" column
 * while below the cap. The card fills its column minus the design's inset on each side, so the gap
 * between cards is two insets and card edges stay on the cell dividers.
 */
export const PlanColumns = ({
  plans,
  bestPremiumIndex,
  addSlots,
  addLabel,
  layout,
  ctaFor,
  onRemove,
  onAddPlan,
  onCtaClick,
  staticText,
  labelSlot,
  compact,
  placeholders = 0,
}: PlanColumnsProps) => {
  // Printed in front of a premium whose quote carries no currency of its own, as on the card.
  const { currencyDisplay } = useCmsClientContext();

  return (
    // From 1280 the ruled table below draws a 1px frame inside its width; the same 1px inset here
    // keeps card edges on the cell dividers, as the ticket's grid requires.
    <div
      data-component="compare-plan-columns"
      className="flex flex-wrap lg:flex-nowrap px-px"
    >
      {/* The label column exists only from 1280; below it there is nothing beside the cards. */}
      <div
        className={compareLabelClass(
          classNames(
            'hidden lg:block',
            // Beside the cards the trip box sits flush left, so its frame lines up with the page title,
            // 8px in from the right and 10px short of the column's foot.
            // In the drawer the unframed box fills the column and pads itself.
            !compact && 'pr-spacing/gap/soft-8px pb-[10px]',
          ),
        )}
      >
        {labelSlot}
      </div>
      <CompareRail data-component="compare-plan-rail">
        {plans.map((plan, index) => {
          // A plan with no uid cannot be identified in the selection, so its remove control would
          // be dead. The page filters those out before rendering; this is the belt to that braces.
          const id = compareIdOf(plan.product);
          if (!id) return null;
          const name = plan.product.product_name ?? plan.product.title ?? id;
          const { offered, original } = formatPremium(plan, currencyDisplay);
          const best = bestPremiumIndex === index;
          const { label: ctaLabel, tone } = ctaFor(plan);
          const issuer = plan.product.provider?.provider_name;
          const picture = compareImageOf(plan.product, layout.columnImage);
          const logo = picture?.src;
          const logoAlt = picture?.isProductImage ? name : (issuer ?? '');
          return (
            <div
              key={id}
              className={compareColumnClass(
                classNames(
                  'relative px-spacing/gap/soft-8px',
                  compact
                    ? 'py-spacing/padding/y-sm'
                    : 'py-spacing/gap/soft-12px lg:py-spacing/gap/soft-8px',
                ),
              )}
              data-component="compare-plan-column"
              data-product-id={id}
            >
              <div
                className={classNames(
                  'flex h-full flex-col items-center gap-spacing/gap/y-sm bg-fill/card px-spacing/gap/x-sm',
                  !compact &&
                    'rounded-radius/card border border-borders/default py-spacing/gap/y-sm',
                  // The drawer's design sets the CTA 12px under the price, the name 16px above it.
                  compact && 'lg:gap-spacing/gap/soft-12px',
                )}
              >
                {!compact && logo && (
                  <div className="flex h-12 w-[122px] items-center justify-center rounded-radius/button bg-fill/card p-spacing/padding/soft-12px lg:h-16 lg:w-full">
                    <CmsImage
                      src={logo}
                      alt={logoAlt}
                      width={106}
                      height={32}
                      sizes={{ _: 106, lg: 186 }}
                      className="size-full object-contain"
                    />
                  </div>
                )}
                {/* Names are never truncated: the block holds the design's lines, and grows. */}
                <div
                  className={classNames(
                    'flex w-full items-center justify-center text-center text-font-size/table-cell font-bold leading-line-height/table-cell text-text/primary',
                    // In the drawer the name takes the slack of a taller neighbour, as the design
                    // does, so the price and CTA sit on one line across columns.
                    compact
                      ? 'min-h-[34px] flex-1 lg:mb-1'
                      : 'min-h-17 lg:min-h-[54px]',
                  )}
                >
                  {name}
                </div>
                {layout.issuerLine && issuer && (
                  <div
                    data-component="compare-plan-issuer"
                    className="mhg-alpha-typography-caption -mt-spacing/gap/y-xs text-center text-text/secondary-lighter"
                  >
                    {issuer}
                  </div>
                )}
                {offered && (
                  <div
                    className="flex flex-col items-center gap-spacing/gap/y-xs"
                    data-best={best || undefined}
                  >
                    <span
                      className={classNames(
                        compact
                          ? 'mhg-alpha-typography-title-2'
                          : 'mhg-alpha-typography-title-1',
                        best ? 'text-text/accent' : 'text-text/primary',
                      )}
                    >
                      {offered}
                    </span>
                    {!compact && original && (
                      <span className="mhg-alpha-typography-caption text-text/disabled line-through">
                        {original}
                      </span>
                    )}
                  </div>
                )}
                <ApplyCtaLink
                  product={plan.product}
                  onClick={() => onCtaClick?.(plan, index, ctaLabel)}
                  className={classNames(
                    'mhg-alpha-typography-button-2 mt-auto flex h-[30px] w-full lg:h-9 items-center justify-center whitespace-nowrap rounded-radius/button border bg-buttons/base px-2',
                    tone === 'cta'
                      ? 'border-buttons/cta-hover text-buttons/cta'
                      : 'border-buttons/primary text-buttons/primary',
                  )}
                >
                  {ctaLabel}
                </ApplyCtaLink>
              </div>
              {!compact && (
                <button
                  type="button"
                  aria-label={tr('compare_remove_plan', staticText, {
                    'plan-name': name,
                  })}
                  onClick={() => onRemove(plan, index)}
                  className="absolute right-0 top-0 flex size-6 items-center justify-center rounded-full bg-text/secondary-lighter text-colors/neutral-white"
                >
                  <MaterialIcon icon="close" px={16} />
                </button>
              )}
            </div>
          );
        })}
        {Array.from({ length: placeholders }, (_, slot) => (
          <div
            // Placeholders stand for no plan yet: the position is the identity.
            // eslint-disable-next-line react/no-array-index-key
            key={`loading-${slot}`}
            data-component="compare-plan-placeholder"
            aria-hidden="true"
            className={compareColumnClass(
              'px-spacing/gap/soft-8px py-spacing/gap/soft-12px lg:py-spacing/gap/soft-8px',
            )}
          >
            {/* The card's own boxes at their sizes: logo, name block, issuer line, price, CTA. Each
                text line takes its height from the typography class the loaded card uses. */}
            <div className="flex h-full flex-col items-center gap-spacing/gap/y-sm rounded-radius/card border border-borders/default bg-fill/card px-spacing/gap/x-sm py-spacing/gap/y-sm">
              <span className="block h-12 w-[122px] animate-pulse rounded-radius/button bg-fill/alternative-3 lg:h-16 lg:w-full" />
              <span className="flex min-h-17 w-full items-center justify-center lg:min-h-[54px]">
                <span className="block h-4 w-3/4 animate-pulse rounded-radius/button bg-fill/alternative-3" />
              </span>
              {layout.issuerLine && (
                <span
                  data-component="compare-plan-placeholder-issuer"
                  className="mhg-alpha-typography-caption -mt-spacing/gap/y-xs block w-1/2"
                >
                  <Pulse className="w-full" />
                </span>
              )}
              {layout.pricesTrip && (
                <span
                  data-component="compare-plan-placeholder-premium"
                  className="flex flex-col items-center gap-spacing/gap/y-xs"
                >
                  <span className="mhg-alpha-typography-title-1 block w-24">
                    <Pulse className="w-full" />
                  </span>
                  <span className="mhg-alpha-typography-caption block w-16">
                    <Pulse className="w-full" />
                  </span>
                </span>
              )}
              <span className="mt-auto block h-[30px] w-full animate-pulse rounded-radius/button bg-fill/alternative-3 lg:h-9" />
            </div>
          </div>
        ))}
        {Array.from({ length: addSlots }, (_, slot) => (
          <div
            // Free places are interchangeable: the position is the identity.
            // eslint-disable-next-line react/no-array-index-key
            key={`add-${slot}`}
            className={compareColumnClass(
              classNames(
                'px-spacing/gap/soft-8px',
                // The same vertical inset as the plan cards beside it, in the drawer too.
                compact
                  ? 'py-spacing/padding/y-sm'
                  : 'py-spacing/gap/soft-12px lg:py-spacing/gap/soft-8px',
              ),
            )}
          >
            <button
              type="button"
              onClick={() => onAddPlan(plans.length + slot)}
              data-component="compare-add-plan"
              className={classNames(
                'mhg-alpha-typography-button-1 flex size-full items-center justify-center gap-spacing/gap/x-xs rounded-radius/card border-dashed border-borders/default bg-fill/card text-buttons/neutral-dark',
                compact ? 'border py-2' : 'min-h-40 border-2 p-3',
              )}
            >
              <MaterialIcon
                icon="add_circle"
                px={22}
                // The desktop design draws it at 24; the inline size needs an important override.
                className="lg:!text-[24px]"
              />
              <span>{addLabel}</span>
            </button>
          </div>
        ))}
      </CompareRail>
    </div>
  );
};
